import React, { useState } from 'react';
import { Building } from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import { createOrganizationProfile } from '../../lib/supabase/organization';
import { useEntity } from '../../hooks/useEntity';

interface CompanySetupFormProps {
  onComplete: () => void;
  onSkip: () => void;
}

export default function CompanySetupForm({ onComplete, onSkip }: CompanySetupFormProps) {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    phoneCountry: 'US',
    address: '',
    type: 'new' as const
  });
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const { user } = useAuthStore();
  const { entity } = useEntity();

  // Phone formatting function
  const formatPhoneNumber = (value: string, country: string) => {
    // Remove all non-digits
    const digits = value.replace(/\D/g, '');
    
    if (country === 'US') {
      // US phone formatting: (XXX) XXX-XXXX
      if (digits.length <= 3) {
        return digits;
      } else if (digits.length <= 6) {
        return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
      } else {
        return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
      }
    } else {
      // International: just add spaces every 3-4 digits
      return digits.replace(/(\d{1,4})(?=\d)/g, '$1 ').trim();
    }
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneNumber(e.target.value, formData.phoneCountry);
    setFormData(prev => ({ ...prev, phone: formatted }));
  };

  const handleCountryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newCountry = e.target.value;
    setFormData(prev => ({ 
      ...prev, 
      phoneCountry: newCountry,
      phone: formatPhoneNumber(prev.phone, newCountry)
    }));
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    
    if (!user) {
      setError('Authentication error. Please try signing in again.');
      return;
    }
    
    if (!entity?.id) {
      setError('Entity not found. Please refresh and try again.');
      return;
    }

    if (!formData.name.trim()) {
      setError('Organization name is required');
      return;
    }

    try {
      setLoading(true);
      // Clean phone number for storage (remove formatting)
      const cleanPhone = formData.phone.replace(/\D/g, '');
      const finalPhone = cleanPhone ? (formData.phoneCountry === 'US' ? `+1${cleanPhone}` : `+${cleanPhone}`) : null;
      
      await createOrganizationProfile({
        name: formData.name.trim(),
        phone: finalPhone,
        address: formData.address.trim() || null,
        type: formData.type,
        owner_id: user.id,
        entity_id: entity.id
      });
      onComplete();
    } catch (error) {
      console.error('Failed to create organization profile:', error);
      if (error instanceof Error) {
        setError(error.message);
      } else {
        setError('Failed to create organization profile. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = () => {
    if (step < 3) {
      setStep(step + 1);
    } else {
      handleSubmit({ preventDefault: () => {} } as React.FormEvent);
    }
  };

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <div className="space-y-4">
            <p className="text-gray-600 mb-4 text-center">
              What's your organization or business name?
            </p>
            <label className="block">
              <span className="text-gray-700 font-medium">Organization Name *</span>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="mt-2 block w-full rounded-lg border border-gray-300 px-4 py-3 focus:border-blue-500 focus:ring-blue-500 text-lg"
                placeholder="Enter organization name"
                disabled={loading}
                autoFocus
              />
            </label>
            <div className="flex space-x-3 pt-4">
              <button
                type="button"
                onClick={() => setStep(2)}
                disabled={!formData.name.trim() || loading}
                className="flex-1 bg-gradient-to-r from-blue-500 to-indigo-600 text-white px-6 py-3 rounded-lg hover:shadow-lg transition-all disabled:opacity-50 font-medium"
              >
                Next
              </button>
              <button
                type="button"
                onClick={onSkip}
                className="px-6 py-3 text-gray-500 hover:bg-gray-50 rounded-lg"
              >
                Skip Setup
              </button>
            </div>
          </div>
        );
      case 2:
        return (
          <div className="space-y-4">
            <p className="text-sm text-gray-600 mb-4">
              Next, let's add a contact phone number. You can skip this for now if you prefer.
            </p>
            <div className="space-y-3">
              <label className="block">
                <span className="text-gray-700">Country/Region</span>
                <select
                  value={formData.phoneCountry}
                  onChange={handleCountryChange}
                  className="mt-1 block w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-blue-500 focus:ring-blue-500"
                  disabled={loading}
                >
                  <option value="US">🇺🇸 United States (+1)</option>
                  <option value="CA">🇨🇦 Canada (+1)</option>
                  <option value="GB">🇬🇧 United Kingdom (+44)</option>
                  <option value="AU">🇦🇺 Australia (+61)</option>
                  <option value="DE">🇩🇪 Germany (+49)</option>
                  <option value="FR">🇫🇷 France (+33)</option>
                  <option value="IT">🇮🇹 Italy (+39)</option>
                  <option value="ES">🇪🇸 Spain (+34)</option>
                  <option value="NL">🇳🇱 Netherlands (+31)</option>
                  <option value="INT">🌍 Other International</option>
                </select>
              </label>
            <label className="block">
              <span className="text-gray-700">Phone Number</span>
                <div className="mt-1 relative">
              <input
                type="tel"
                value={formData.phone}
                    onChange={handlePhoneChange}
                className="mt-1 block w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-blue-500 focus:ring-blue-500"
                    placeholder={formData.phoneCountry === 'US' ? '(555) 123-4567' : 'Enter phone number'}
                disabled={loading}
              />
                  <div className="mt-1 text-xs text-gray-500">
                    {formData.phoneCountry === 'US' ? 'Format: (XXX) XXX-XXXX' : 'International format will be applied'}
                  </div>
                </div>
            </label>
            </div>
            <div className="flex space-x-3">
              <button
                type="button"
                onClick={() => setStep(3)}
                disabled={loading}
                className="flex-1 bg-gradient-to-r from-blue-500 to-indigo-600 text-white px-4 py-2 rounded-lg hover:shadow-lg transition-all disabled:opacity-50"
              >
                Next
              </button>
              <button
                type="button"
                onClick={handleSkip}
                disabled={loading}
                className="px-4 py-2 text-gray-500 hover:bg-gray-50 rounded-lg disabled:opacity-50"
              >
                Skip
              </button>
              <button
                type="button"
                onClick={onSkip}
                className="px-4 py-2 text-gray-500 hover:bg-gray-50 rounded-lg"
              >
                Skip Entire Setup
              </button>
            </div>
          </div>
        );
      case 3:
        return (
          <div className="space-y-4">
            <p className="text-gray-600 mb-4 text-center">
              What's your business address?
            </p>
            <label className="block">
              <span className="text-gray-700 font-medium">Address</span>
              <textarea
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                className="mt-2 block w-full rounded-lg border border-gray-300 px-4 py-3 focus:border-blue-500 focus:ring-blue-500"
                placeholder="Enter organization address"
                rows={3}
                disabled={loading}
              />
            </label>
            <div className="flex space-x-3 pt-4">
              <button
                type="button"
                onClick={handleSubmit}
                disabled={loading}
                className="flex-1 bg-gradient-to-r from-blue-500 to-indigo-600 text-white px-6 py-3 rounded-lg hover:shadow-lg transition-all disabled:opacity-50 font-medium"
              >
                {loading ? 'Saving...' : 'Complete Setup'}
              </button>
              <button
                type="button"
                onClick={handleSkip}
                disabled={loading}
                className="px-6 py-3 text-gray-500 hover:bg-gray-50 rounded-lg disabled:opacity-50"
              >
                Skip
              </button>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 mx-4">
        <div className="text-center mb-6">
          <div className="bg-blue-100 p-3 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
            <Building className="w-8 h-8 text-blue-500" />
          </div>
          <h2 className="text-xl font-bold text-gray-900">Set Up Your Organization</h2>
          <p className="text-gray-600 mt-2">We need some basic information to get started</p>
        </div>
        
      {error && (
        <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-lg text-sm">
          {error}
        </div>
      )}
        
        <div className="mb-4">
          <div className="flex items-center justify-center space-x-2 mb-4">
            {[1, 2, 3].map((stepNum) => (
              <div
                key={stepNum}
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  stepNum === step
                    ? 'bg-blue-500 text-white'
                    : stepNum < step
                    ? 'bg-green-500 text-white'
                    : 'bg-gray-200 text-gray-600'
                }`}
              >
                {stepNum < step ? '✓' : stepNum}
              </div>
            ))}
          </div>
          <p className="text-sm text-gray-500 text-center">
            Step {step} of 3
          </p>
        </div>
        
      {renderStep()}
      </div>
    </div>
  );
}